import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { TotpMultiFactorGenerator, MultiFactorResolver } from 'firebase/auth';
import { typography, baseBrandColors } from '../common/styles/universalTheme';

interface TotpVerificationProps {
  open: boolean;
  resolver: MultiFactorResolver | null;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * TOTP Verification Dialog
 *
 * Prompts users with Firebase MFA enabled to enter their TOTP code
 * from their authenticator app to complete sign-in.
 */
const TotpVerification: React.FC<TotpVerificationProps> = ({
  open,
  resolver,
  onSuccess,
  onCancel,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    if (!resolver || !code.trim()) return;

    setLoading(true);
    setError(null);

    try {
      // Find the TOTP hint
      const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );

      if (!totpHint) {
        setError('No TOTP factor found. Please contact support.');
        setLoading(false);
        return;
      }

      // Generate the assertion with the TOTP code
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        code.trim()
      );

      // Complete the sign-in
      await resolver.resolveSignIn(multiFactorAssertion);

      console.log('✅ MFA verification successful');
      onSuccess();
    } catch (err) {
      console.error('MFA verification error:', err);
      const firebaseErr = err as { code?: string; message?: string };

      if (firebaseErr.code === 'auth/invalid-verification-code') {
        setError('Invalid code. Please check and try again.');
      } else if (firebaseErr.code === 'auth/code-expired') {
        setError('Code expired. Please generate a new code.');
      } else {
        setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerify();
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { bgcolor: 'background.paper' }
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          pb: 1,
          color: 'text.primary',
          fontSize: typography.fontSize.xl,
          fontWeight: typography.fontWeight.semibold,
        }}
      >
        Two-Factor Authentication
      </DialogTitle>

      <DialogContent>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: typography.fontSize.sm,
            mb: 3,
          }}
        >
          Enter the 6-digit code from your authenticator app to complete sign-in.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, bgcolor: 'background.default' }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          label="Verification Code"
          value={code}
          onChange={(e) => {
            // Only allow digits, max 6 characters
            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
            setCode(value);
          }}
          onKeyDown={handleKeyDown}
          disabled={loading}
          placeholder="000000"
          inputProps={{
            maxLength: 6,
            style: {
              textAlign: 'center',
              letterSpacing: '0.5em',
              fontSize: typography.fontSize.xl,
              fontFamily: typography.fontFamily.mono,
            },
          }}
          sx={{ mb: 2 }}
        />

        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: typography.fontSize.xs,
            textAlign: 'center',
          }}
        >
          Open your authenticator app (Google Authenticator, Authy, etc.) to get your code.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            fontSize: typography.fontSize.sm,
            '&:hover': { borderColor: 'text.primary', bgcolor: 'transparent' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerify}
          disabled={loading || code.length !== 6}
          variant="contained"
          sx={{
            bgcolor: baseBrandColors.buttons.green.main,
            color: '#FFFFFF',
            fontSize: typography.fontSize.sm,
            minWidth: 100,
            '&:hover': { bgcolor: baseBrandColors.buttons.green.hover },
            '&:disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TotpVerification;
